import {
  Button,
  Checkbox,
  Flex,
  Grid,
  Label,
  Skeleton,
  Switch,
  Text,
} from "@suns/design-system";
import {
  PROJECTION_SEASON_RANGES,
  SEASON_RANGES,
} from "@/shared/utils/nba-season";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import {
  PlayerMetadataRow,
  PlayerRow,
} from "@suns/api/generated-client/apollo";
import { useState } from "react";
import { formatPlayerChartData } from "@/shared/utils/helper-functions";
import { StatName, Timeframe } from "@/shared/const";
import { theme } from "@/shared/theme";
import MetricChartContainer from "../../MetricChartContainer";
import { useAsync } from "@/shared/hooks";
import { SunsApiError } from "@suns/api";
import { viewPlayerLoader } from "../../../loaders/player-loader";

dayjs.extend(isBetween);

const calculateValueRange = (
  lower: string | number | null,
  upper: string | number | null
) => {
  if (!lower || !upper) return null;
  return Number(lower) - Number(upper);
};

export function NbaPlayerAnalytics({
  player,
  print = false,
}: {
  player: PlayerRow;
  print?: boolean;
}) {
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<
    Record<"season" | "projection", Timeframe>
  >({
    // @ts-expect-error - DO NOT COPY THIS LINE
    season: SEASON_RANGES[SEASON_RANGES.length - 1].label,
    projection:
      // @ts-expect-error - DO NOT COPY THIS LINE
      PROJECTION_SEASON_RANGES[PROJECTION_SEASON_RANGES.length - 1].label,
  });
  const [showFutureProjections, setshowFutureProjections] = useState(print);
  const [showAdjustedPpv, setShowAdjustedPpv] = useState(true);

  const { response, loading, error } = useAsync(viewPlayerLoader, {
    playerId: player.id,
    metadataHistory: true,
  });

  if (loading)
    return (
      <Flex direction="down" gap="lg" className="w-full">
        <Flex direction="right" gap="sm">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-8 w-40" />
        </Flex>
        <Flex direction="right" gap="lg" className="w-full">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
        </Flex>
      </Flex>
    );

  if (error) {
    throw new SunsApiError("Unable to load player metadata.", {
      cause: error,
    });
  }

  const playerMetadata = response?.player?.metadata;
  const playerProjectionMetadata = player?.projectionMetadata;

  const seasonRange = SEASON_RANGES.find(
    (season) => season.label === selectedTimeFrame.season
  );

  const projectionSeasonRange = PROJECTION_SEASON_RANGES.find(
    (season) => season.label === selectedTimeFrame.projection
  );

  const filterMetadataByDateRange = <T extends { snapshotDate: string | null }>(
    metadata: T[] | undefined,
    range: { start: string; end: string } | undefined
  ) => {
    return metadata?.filter((item) => {
      const date = item.snapshotDate;
      return dayjs(date).isBetween(range?.start, range?.end, null, "[]");
    });
  };

  const filterProjectionMetadataByDateRange = <
    T extends { projectionDate: string | null },
  >(
    metadata: T[] | undefined,
    range: { start: string; end: string } | undefined
  ) => {
    return metadata?.filter((item) => {
      const date = item.projectionDate;
      return dayjs(date).isBetween(range?.start, range?.end, null, "[]");
    });
  };

  const filteredPlayerMetadata = filterMetadataByDateRange(
    selectedTimeFrame.season === Timeframe.FIVE_SEASONS ||
      selectedTimeFrame.season === Timeframe.MAX
      ? playerMetadata.filter(
          (item: PlayerMetadataRow) => item.analyticGradeCurrent !== null
        )
      : playerMetadata,
    seasonRange
  );

  const filteredPlayerProjectionMetadata = filterProjectionMetadataByDateRange(
    playerProjectionMetadata,
    projectionSeasonRange
  );

  const hasMetadata = (playerMetadata?.length ?? 0) > 0;
  if (!hasMetadata) {
    return (
      <Text muted size="lg" className="py-4 text-center">
        No analytic data available.
      </Text>
    );
  }

  const timeFrames = SEASON_RANGES.map((season) => season.label);

  const handleshowFutureProjections = () => {
    setshowFutureProjections(!showFutureProjections);
  };

  const allPlayerMetadata = [
    ...(filteredPlayerMetadata ?? []),
    ...(filteredPlayerProjectionMetadata ?? []),
  ];

  const formattedPlayerMetadata = formatPlayerChartData(
    allPlayerMetadata as PlayerMetadataRow[],
    selectedTimeFrame.season
  );

  const filteredPlayerAimMetadata = showFutureProjections
    ? formattedPlayerMetadata.filter((data) => {
        const firstSnapshotDate = formattedPlayerMetadata.find(
          (data) => data.aim !== null
        )?.snapshotDate;
        const isPastDate = dayjs(data.snapshotDate).isBefore(
          dayjs(firstSnapshotDate)
        );
        return !isPastDate || data.aim !== null;
      })
    : formattedPlayerMetadata;

  const filteredPlayerApolloGradeMetadata = showFutureProjections
    ? formattedPlayerMetadata.filter((data) => {
        const firstSnapshotDate = formattedPlayerMetadata.find(
          (data) => data.analytics !== null
        )?.snapshotDate;
        const isPastDate = dayjs(data.snapshotDate).isBefore(
          dayjs(firstSnapshotDate)
        );
        return !isPastDate || data.analytics !== null;
      })
    : formattedPlayerMetadata;

  const filteredPlayerPpvMetadata = showFutureProjections
    ? formattedPlayerMetadata.filter((data) => {
        const currentYear = dayjs().year();
        const isAfterCurrentYear =
          dayjs(data.snapshotDate).year() > currentYear;
        return !isAfterCurrentYear && data.ppv !== null;
      })
    : formattedPlayerMetadata;

  const filteredPlayerPrvMetadata = showFutureProjections
    ? formattedPlayerMetadata.filter((data) => {
        const currentYear = dayjs().year();
        const isAfterCurrentYear =
          dayjs(data.snapshotDate).year() > currentYear;
        return !isAfterCurrentYear || data.prv !== null;
      })
    : formattedPlayerMetadata;

  const availableCharts = [
    {
      title: "Apollo Grade Value",
      data: filteredPlayerApolloGradeMetadata.map((data) => ({
        ...data,
        analytics_lowerBound: data.lowerGrade ?? null,
        analytics_upperBound: data.upperGrade ?? null,
        analytics_value_range: calculateValueRange(
          data.lowerGrade ?? null,
          data.upperGrade ?? null
        ),
        snapshotDate: data.snapshotDate ?? "",
      })),
      config: {
        hybrid: {
          label: "Hybrid",
          color: theme.colors.purple.DEFAULT,
        },
        analytics: {
          label: "Analytics",
          color: theme.colors.magenta.DEFAULT,
        },
        scout: {
          label: "Scout",
          color: theme.colors.teal.DEFAULT,
        },
        analytics_upperBound: {
          label: "Upper Analytics Grade",
          color: theme.colors.magenta.DEFAULT,
          isUpperBound: true,
        },
        analytics_lowerBound: {
          label: "Lower Analytics Grade",
          color: theme.colors.magenta.DEFAULT,
          isLowerBound: true,
        },
        analytics_value_range: {
          label: `Grade Value Range`,
          color: "transparent",
          isValueRange: true,
        },
      } as Record<string, { label: string; color: string }>,
      chartType: null,
      showFutureProjections: showFutureProjections,
      timeframe: selectedTimeFrame.season,
      activeChart: "analytics",
      type: "composed chart" as const,
      yAxisConfig: {
        minValue: 1,
        maxValue: 9,
        customTick: (
          <ApolloGradeCustomTick
            height={100}
            x={0}
            y={0}
            payload={{ value: 1 }}
            visibleTicksCount={10}
          />
        ),
      },
    },

    {
      title: `Apollo Impact Metric (${StatName.Aim})`,
      data: filteredPlayerAimMetadata.map((data) => ({
        ...data,
        aim_upperBound: data.upperAim ?? null,
        aim_lowerBound: data.lowerAim ?? null,
        aim_value_range:
          (data.lowerAim ?? null) !== null &&
          (data.upperAim ?? null) !== null &&
          Number(data.lowerAim) - Number(data.upperAim) === 0
            ? null
            : (data.lowerAim ?? null) !== null &&
                (data.upperAim ?? null) !== null
              ? Number(data.lowerAim) - Number(data.upperAim)
              : null,
        oAim_upperBound: data.upperOAim ?? null,
        oAim_lowerBound: data.lowerOAim ?? null,
        oAim_value_range:
          (data.lowerOAim ?? null) !== null &&
          (data.upperOAim ?? null) !== null &&
          Number(data.lowerOAim) - Number(data.upperOAim) === 0
            ? null
            : (data.lowerOAim ?? null) !== null &&
                (data.upperOAim ?? null) !== null
              ? Number(data.lowerAim) - Number(data.upperAim)
              : null,
        dAim_upperBound: data.upperDAim ?? null,
        dAim_lowerBound: data.lowerDAim ?? null,
        dAim_value_range:
          (data.lowerDAim ?? null) !== null &&
          (data.upperDAim ?? null) !== null &&
          Number(data.lowerDAim) - Number(data.upperDAim) === 0
            ? null
            : (data.lowerDAim ?? null) !== null &&
                (data.upperDAim ?? null) !== null
              ? Number(data.lowerDAim) - Number(data.upperDAim)
              : null,
        snapshotDate: data.snapshotDate ?? "",
      })),
      config: {
        aim: {
          label: StatName.Aim,
          color: theme.colors.purple.DEFAULT,
        },
        aim_upperBound: {
          label: `Upper ${StatName.Aim}`,
          color: theme.colors.purple.DEFAULT,
          isUpperBound: true,
        },
        aim_lowerBound: {
          label: `Lower ${StatName.Aim}`,
          color: theme.colors.purple.DEFAULT,
          isLowerBound: true,
        },
        aim_value_range: {
          label: `${StatName.Aim} Value Range`,
          color: "transparent",
          isValueRange: true,
        },
        oAim: {
          label: StatName.OAim,
          color: theme.colors.magenta.DEFAULT,
        },
        oAim_upperBound: {
          label: `Upper ${StatName.OAim}`,
          color: theme.colors.magenta.DEFAULT,
          isUpperBound: true,
        },
        oAim_lowerBound: {
          label: `Lower ${StatName.OAim}`,
          color: theme.colors.magenta.DEFAULT,
          isLowerBound: true,
        },
        oAim_value_range: {
          label: `${StatName.OAim} Value Range`,
          color: "transparent",
          isValueRange: true,
        },
        dAim: {
          label: StatName.DAim,
          color: theme.colors.teal.DEFAULT,
        },
        dAim_upperBound: {
          label: `Upper ${StatName.DAim}`,
          color: theme.colors.teal.DEFAULT,
          isUpperBound: true,
        },
        dAim_lowerBound: {
          label: `Lower ${StatName.DAim}`,
          color: theme.colors.teal.DEFAULT,
          isLowerBound: true,
        },
        dAim_value_range: {
          label: `${StatName.DAim} Value Range`,
          color: "transparent",
          isValueRange: true,
        },
      } as Record<string, { label: string; color: string }>,
      chartType: null,
      showFutureProjections: showFutureProjections,
      timeframe: selectedTimeFrame.season,
      activeChart: "aim",
      type: "composed chart" as const,
      yAxisConfig: {
        maxValue: 8,
        minTickCount: 9,
      },
    },

    {
      title: `Player Performance Value (PPV)`,
      data: filteredPlayerPpvMetadata
        .filter((data) => data.ppv !== null)
        .map((data) => ({
          ...data,
          ppv: showAdjustedPpv
            ? data.adjustedPpv ?? data.ppv ?? null
            : data.ppv ?? null,
          oPpv: showAdjustedPpv
            ? data.adjustedOPpv ?? data.oPpv ?? null
            : data.oPpv ?? null,
          dPpv: showAdjustedPpv
            ? data.adjustedDPpv ?? data.dPpv ?? null
            : data.dPpv ?? null,
          snapshotDate: data.snapshotDate ?? "",
        })),
      config: {
        ppv: {
          label: StatName.PPV,
          color: theme.colors.purple.DEFAULT,
        },
        oPpv: {
          label: StatName.OPPV,
          color: theme.colors.magenta.DEFAULT,
        },
        dPpv: {
          label: StatName.DPPV,
          color: theme.colors.teal.DEFAULT,
        },
      } as Record<string, { label: string; color: string }>,
      chartType: "currency",
      showFutureProjections: showFutureProjections,
      timeframe: selectedTimeFrame.season,
      activeChart: "ppv",
      type: "composed chart" as const,
      adjustedControls: !print && (
        <Flex direction="right" gap="sm" align="center">
          <Text size="xs" heading>
            Inflation Adjusted
          </Text>
          <Switch
            checked={showAdjustedPpv}
            onCheckedChange={setShowAdjustedPpv}
          />
        </Flex>
      ),
    },
    {
      title: `Player Relative Value (PRV)`,
      data: filteredPlayerPrvMetadata
        .filter((data) => data.prv !== null)
        .map((data) => ({
          ...data,
          snapshotDate: data.snapshotDate ?? "",
        })),
      config: {
        prv: {
          label: StatName.PRV,
          color: theme.colors.purple.DEFAULT,
        },
      } as Record<string, { label: string; color: string }>,
      chartType: "currency",
      showFutureProjections: showFutureProjections,
      timeframe: selectedTimeFrame.season,
      activeChart: "prv",
      type: "area chart" as const,
    },
  ];

  return (
    <>
      <Flex direction="down" gap="lg">
        {!print && (
          <Flex direction="down" gap="md">
            <Flex gap="sm">
              {timeFrames.map((timeFrame) => (
                <Flex key={timeFrame} align="center">
                  <Button
                    key={timeFrame}
                    variant={
                      selectedTimeFrame.season === timeFrame
                        ? "default"
                        : "ghost"
                    }
                    onClick={() =>
                      setSelectedTimeFrame({
                        season: timeFrame,
                        projection: timeFrame,
                      })
                    }
                    size="xs"
                  >
                    <Text size="sm">{timeFrame}</Text>
                  </Button>
                </Flex>
              ))}
            </Flex>
            <Label className="cursor-pointer">
              <Checkbox
                checked={showFutureProjections}
                onCheckedChange={handleshowFutureProjections}
              />
              <div className="grid gap-1.5 font-normal">
                <p className="text-sm font-medium leading-none">
                  Show Remaining Capacity
                </p>
              </div>
            </Label>
          </Flex>
        )}
        <Grid columns={print ? ["2"] : ["1", "lg:2"]} gap={print ? "md" : "lg"}>
          {availableCharts.map((chart) => (
            <MetricChartContainer key={chart.title} {...chart} print={print} />
          ))}
        </Grid>
      </Flex>
    </>
  );
}

interface ApolloGradeCustomTickProps {
  height: number;
  x: number;
  y: number;
  payload: { value: number };
  visibleTicksCount: number;
}

const gradeLabels: Record<number, string> = {
  1: "ML",
  2: "RS",
  3: "RT",
  4: "TR",
  5: "ST",
  6: "AS",
  7: "AS+",
  8: "FR",
};

interface ApolloGradeCustomTickProps {
  height: number;
  x: number;
  y: number;
  payload: { value: number };
  visibleTicksCount: number;
}

function ApolloGradeCustomTick(props: ApolloGradeCustomTickProps) {
  const { x, y, payload, visibleTicksCount, height } = props;

  const tickHeight = height / visibleTicksCount;
  const label = gradeLabels[payload.value];

  return (
    <g transform={`translate(${x},${y - tickHeight / 2})`}>
      <text x={0} y={0} textAnchor="end" fill="#666">
        {label}
      </text>
    </g>
  );
}
